/* eslint-disable react-hooks/exhaustive-deps */
import { ModalGlobal } from '../modal/modal.Global';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { update } from '../../services/item';
import axios from 'axios';
import { SearchInput } from '../search/search.Input';
import { FaTimes } from 'react-icons/fa';
import useItems from '../../hooks/useItems';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BtnSimple } from '../form/btn.Simple';
import { Checkbox } from '../form/checkbox';
import {
  buildSecondaryWidgetOptions,
  getDefaultLinkDisplayOptions,
  LinkDisplayOptionsEditor,
  mergeLinkDisplayOptions,
} from '../link/link.DisplayOptions';
import { ColorsSwatch } from '../form/colorsSwatch';
import { FitOptionsButtons, MaskControls } from '../form/mask.Controls';
import { PicSelectionOptions } from '../pic/pic.Selector';
import {
  WidgetSettingsRoot,
  WidgetSettingsCollapsibleSection,
  SettingsInput,
  SettingsSelect,
  SettingsRow,
  SettingsTextArea,
  DangerZone,
} from '../form/settings.Widget';

export const FilterSettings = props => {
  const { itemId, filterId, filter, displayType, boardTabId } = props;

  const [item] = useItems([itemId]);
  const filterItems = useItems(filter?.items);
  const excludeFilterItems = useItems(filter?.exclude);
  const currentTab = useSelector(state => state.global.currentTab);
  const widgetTabId = boardTabId || currentTab;
  const tab = useSelector(state => state.items?.[widgetTabId]);
  const widgetConfig = useMemo(() => {
    if (!tab?.grid) {
      return {};
    }
    const entry = tab.grid.find(element => getId(element) === filterId);
    return entry?.[filterId] || {};
  }, [tab?.grid, filterId]);

  const [filteredItemsForTabs, setFilteredItemsForTabs] = useState([]);
  const tabsFromFilteredItems = useMemo(() => {
    const tabIds = new Set();
    filteredItemsForTabs.forEach(filteredItem => {
      if (!Array.isArray(filteredItem?.tabs)) {
        return;
      }
      filteredItem.tabs.forEach(tabObj => {
        const tabId = getId(tabObj);
        const display = tabObj?.[tabId]?.display;
        if (tabId && (!display || display === 'grid')) {
          tabIds.add(tabId);
        }
      });
    });
    return Array.from(tabIds);
  }, [filteredItemsForTabs]);
  const tabs = useItems(tabsFromFilteredItems);
  const tabLabelById = useMemo(() => {
    const map = new Map();
    tabs?.forEach(tabItem => {
      if (tabItem?._id) {
        map.set(tabItem._id, tabItem.label);
      }
    });
    return map;
  }, [tabs]);
  const maskPreviewItem =
    filteredItemsForTabs?.[0]?._id
      ? filteredItemsForTabs[0]
      : filterItems?.[0]?._id
        ? filterItems[0]
        : null;
  const maskOptions = useMemo(() => {
    const options = [{ key: 'none', label: 'Aucun masque' }];
    (maskPreviewItem?.pics || []).forEach((picObj, picIndex) => {
      const picId = getId(picObj);
      const payload = picObj?.[picId] || {};
      options.push({
        key: picIndex,
        label: payload?.label || `Image ${picIndex + 1}`,
        previewUrl: payload?.url || null,
        iconSlug: payload?.iconSlug || null,
      });
    });
    return options;
  }, [maskPreviewItem?.pics]);
  const widgetOptions = useMemo(
    () => buildSecondaryWidgetOptions(maskPreviewItem),
    [maskPreviewItem],
  );

  const [fullTabId, setFullTabId] = useState('sidebar');
  const itemStyle = widgetConfig?.itemStyle || filter?.itemStyle || {};
  const fullTabOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    tabsFromFilteredItems.forEach(tabId => {
      const rawLabel = tabLabelById.get(tabId) || '';
      const label = String(rawLabel).trim();
      const value = label ? `label:${label}` : tabId;
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      options.push({
        value,
        label: label || tabId,
      });
    });
    return options;
  }, [tabsFromFilteredItems, tabLabelById]);

  useEffect(() => {
    if (!tab?.grid) {
      return;
    }
    const entry = tab.grid.find(element => element[filterId]);
    const stored = entry?.[filterId]?.fullTabId || 'sidebar';
    if (stored === 'sidebar' || String(stored).startsWith('label:')) {
      setFullTabId(stored);
      return;
    }
    const label = tabLabelById.get(stored);
    if (label) {
      setFullTabId(`label:${label}`);
      return;
    }
    setFullTabId(stored);
  }, [tab, filterId, tabLabelById]);

  useEffect(() => {
    const fetchFilteredItems = async () => {
      if (!Array.isArray(filter?.items) || filter.items.length === 0) {
        setFilteredItemsForTabs([]);
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/filter`,
          {
            params: {
              items: filter?.items.join(','),
              exclude: filter?.exclude?.join(','),
            },
          }
        );
        setFilteredItemsForTabs(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setFilteredItemsForTabs([]);
      }
    };
    fetchFilteredItems();
  }, [filter?.items, filter?.exclude]);
  const deleteFilter = () => {
    if (!item?.filters) return;

    const clonedFilters = deepCloneArray(item.filters);
    const filterIdx = clonedFilters.findIndex(
      filterObj => getId(filterObj) === filterId,
    );
    if (filterIdx > -1) {
      clonedFilters.splice(filterIdx, 1);
      update(`items/${itemId}`, 'filters', clonedFilters);
    }
  };

  const updateField = (value_, field_) => {
    if (!item?.filters) return;
    const currentValue = filter?.[field_];
    if (serialize(currentValue) === serialize(value_)) {
      return;
    }
    const clonedFilters = deepCloneArray(item.filters);
    const filterIdx = clonedFilters.findIndex(
      filterObj => getId(filterObj) === filterId,
    );
    if (filterIdx > -1) {
      clonedFilters[filterIdx][filterId][field_] = value_;
      update(`items/${itemId}`, 'filters', clonedFilters);
    }
  };

  const updateGridField = (value_, field_) => {
    if (!tab?.grid || !widgetTabId) return;
    const clonedGrid = deepCloneArray(tab.grid);
    const gridIdx = clonedGrid.findIndex(
      gridObj => getId(gridObj) === filterId,
    );
    if (gridIdx > -1) {
      const gridId = getId(clonedGrid[gridIdx]);
      clonedGrid[gridIdx][gridId][field_] = value_;
      update(`items/${widgetTabId}`, 'grid', clonedGrid);
    }
  };


  const addItem = item_ => {
    const current = Array.isArray(filter?.items) ? filter.items : [];
    updateField([...current, item_._id], 'items');
  };

  const addExcludeItem = item_ => {
    const current = Array.isArray(filter?.exclude) ? filter.exclude : [];
    updateField([...current, item_._id], 'exclude');
  };

  const addCurrentItemToList = () => {
    if (!itemId) return;
    const current = Array.isArray(filter?.items) ? filter.items : [];
    if (current.includes(itemId)) return;
    updateField([...current, itemId], 'items');
  };

  const addCurrentItemToExclude = () => {
    if (!itemId) return;
    const current = Array.isArray(filter?.exclude) ? filter.exclude : [];
    if (current.includes(itemId)) return;
    updateField([...current, itemId], 'exclude');
  };

  const removeCurrentItem = () => {
    if (!itemId) return;
    const nextItems = Array.isArray(filter?.items)
      ? filter.items.filter(id => id !== itemId)
      : [];
    const nextExclude = Array.isArray(filter?.exclude)
      ? filter.exclude.filter(id => id !== itemId)
      : [];
    updateField(nextItems, 'items');
    updateField(nextExclude, 'exclude');
  };

  const displayTypeLink = displayType === 'massonery' ? 'réduit' : displayType;
  const [elementsToDisplay, setElementsToDisplay] = useState(
    mergeLinkDisplayOptions(
      getDefaultLinkDisplayOptions(displayType),
      widgetConfig?.elementsToDisplay || filter?.elementsToDisplay,
    ),
  );
  const syncingDisplayFromFilterRef = useRef(false);

  useEffect(() => {
    syncingDisplayFromFilterRef.current = true;
    setElementsToDisplay(
      mergeLinkDisplayOptions(
        getDefaultLinkDisplayOptions(displayType),
        widgetConfig?.elementsToDisplay || filter?.elementsToDisplay,
      ),
    );
  }, [widgetConfig?.elementsToDisplay, filter?.elementsToDisplay, filterId, displayType]);

  useEffect(() => {
    if (syncingDisplayFromFilterRef.current) {
      syncingDisplayFromFilterRef.current = false;
      return;
    }
    const mergedFromFilter = mergeLinkDisplayOptions(
      getDefaultLinkDisplayOptions(displayType),
      widgetConfig?.elementsToDisplay || filter?.elementsToDisplay,
    );
    if (serialize(mergedFromFilter) === serialize(elementsToDisplay)) {
      return;
    }
    updateGridField(elementsToDisplay, 'elementsToDisplay');
  }, [elementsToDisplay, widgetConfig?.elementsToDisplay, filter?.elementsToDisplay, displayType]);

  return (
    <ModalGlobal
      title='edition'
      idToOpen={filterId}
    >
      <WidgetSettingsRoot>
        <WidgetSettingsCollapsibleSection title='Général' defaultOpen={true}>
          <SettingsInput
            placeholder={'label'}
            initValue={filter?.label}
            onBlur={value => updateField(value, 'label')}
          />
          <Checkbox
            label="Afficher le bouton + sur le widget filtre"
            value={filter?.showAddItemButton === true}
            onChange={value => updateField(value, 'showAddItemButton')}
          />
          <Checkbox
            label='Afficher les labels des items liés'
            value={filter?.showItemsLabel === true}
            onChange={value => updateField(value, 'showItemsLabel')}
          />
          <SettingsRow label='Message (vide)' dense>
            <SettingsTextArea
              placeholder={"Markdown — message quand aucun item n'est affiché"}
              initValue={filter?.emptyLabel}
              onBlur={value => updateField(value, 'emptyLabel')}
              rows={3}
              maxRows={8}
            />
          </SettingsRow>
        </WidgetSettingsCollapsibleSection>

        {displayTypeLink === 'réduit' && (
          <WidgetSettingsCollapsibleSection
            title='Affichage (mode réduit)'
            defaultOpen={false}
          >
            <LinkDisplayOptionsEditor
              listToDisplay={elementsToDisplay}
              setListToDisplay={setElementsToDisplay}
              maskOptions={maskOptions}
              widgetOptions={widgetOptions}
            />
            <SettingsRow label="Taille de l'avatar" dense>
              <SettingsSelect
                value={String(
                  Number.isFinite(Number(widgetConfig?.reducedMinAvatarSize))
                    ? Number(widgetConfig.reducedMinAvatarSize)
                    : Number.isFinite(Number(filter?.reducedMinAvatarSize))
                      ? Number(filter.reducedMinAvatarSize)
                    : 96
                )}
                onChange={event =>
                  updateGridField(
                    Number(event.target.value),
                    'reducedMinAvatarSize',
                  )
                }
              >
                <option value='96'>md</option>
                <option value='50'>sm</option>
                <option value='160'>lg</option>
              </SettingsSelect>
            </SettingsRow>
            <div className='mt-05'>
              <Checkbox
                label="Activer une note (nombre) par item affiché"
                value={filter?.noteNumEnabled === true}
                onChange={value => updateField(value, 'noteNumEnabled')}
              />
            </div>
            {filter?.noteNumEnabled === true && (
              <SettingsRow
                label='Position note'
                dense
              >
                <SettingsSelect
                  value={filter?.noteNumPosition || 'top-right'}
                  onChange={event =>
                    updateField(event.target.value, 'noteNumPosition')
                  }
                >
                  <option value='top-right'>Haut droite</option>
                  <option value='top-left'>Haut gauche</option>
                  <option value='bottom-right'>Bas droite</option>
                  <option value='bottom-left'>Bas gauche</option>
                </SettingsSelect>
              </SettingsRow>
            )}
          </WidgetSettingsCollapsibleSection>
        )}

        <WidgetSettingsCollapsibleSection
          title='Mise en page des items'
          defaultOpen={false}
        >
          {displayType === 'simple' && (
            <SettingsRow label='Espacement des lignes' dense>
              <SettingsSelect
                value={
                  widgetConfig?.simpleGapSize === 'sm' ||
                  widgetConfig?.simpleGapSize === 'md' ||
                  widgetConfig?.simpleGapSize === 'lg'
                    ? widgetConfig.simpleGapSize
                    : 'lg'
                }
                onChange={event =>
                  updateGridField(event.target.value, 'simpleGapSize')
                }
              >
                <option value='sm'>sm (gap-0)</option>
                <option value='md'>md (gap-1)</option>
                <option value='lg'>lg (gap-2)</option>
              </SettingsSelect>
            </SettingsRow>
          )}
          <SettingsRow label='Items par ligne' dense>
            <SettingsSelect
              value={String(
                Math.max(
                  1,
                  Number.isFinite(Number(widgetConfig?.itemsPerRow))
                    ? Number(widgetConfig.itemsPerRow)
                    : Number.isFinite(Number(filter?.itemsPerRow))
                      ? Number(filter.itemsPerRow)
                    : 4,
                ),
              )}
              onChange={event =>
                updateGridField(Number(event.target.value), 'itemsPerRow')
              }
            >
              <option value='1'>1</option>
              <option value='2'>2</option>
              <option value='3'>3</option>
              <option value='4'>4</option>
              <option value='5'>5</option>
              <option value='6'>6</option>
              <option value='8'>8</option>
              <option value='10'>10</option>
              <option value='12'>12</option>
            </SettingsSelect>
          </SettingsRow>

          <WidgetSettingsCollapsibleSection
            title='Style des items'
            defaultOpen={false}
            className='border-primary border-opacity-10'
          >
            <SettingsRow label='Case'>
              <SettingsSelect
                value={itemStyle?.displayCase || 'case pleine largeur'}
                onChange={e =>
                  updateGridField(
                    {
                      ...itemStyle,
                      displayCase: e.target.value,
                    },
                    'itemStyle',
                  )
                }
              >
                <option value='case pleine largeur'>Case pleine largeur</option>
                <option value='case arrondie'>Case arrondie</option>
                <option value='masque'>Masque</option>
              </SettingsSelect>
            </SettingsRow>

            <SettingsRow label='Couleur fond' dense>
              <ColorsSwatch
                currentColor={itemStyle?.bgColor || 'transparent'}
                colors={[
                  'transparent',
                  'primary',
                  'secondary-d',
                  'red',
                  'orange',
                  'yellow',
                  'lime',
                  'green',
                  'blue',
                  'purple',
                  'pink',
                ]}
                onClick={color =>
                  updateGridField(
                    {
                      ...itemStyle,
                      bgColor: color === 'transparent' ? null : color,
                    },
                    'itemStyle',
                  )
                }
              />
            </SettingsRow>

            <PicSelectionOptions
              title='Image de fond'
              options={maskOptions}
              value={
                Number.isFinite(itemStyle?.bgPicIndex)
                  ? itemStyle.bgPicIndex
                  : 'none'
              }
              size={28}
              onChange={value =>
                updateGridField(
                  {
                    ...itemStyle,
                    bgPicIndex: value === 'none' ? null : Number(value),
                  },
                  'itemStyle',
                )
              }
            />

            <SettingsRow label='Affichage fond'>
              <FitOptionsButtons
                value={itemStyle?.bgPicFit || 'content'}
                includeFixed={false}
                includeContent={true}
                fillLabel='Remplir la case'
                onChange={next =>
                  updateGridField(
                    {
                      ...itemStyle,
                      bgPicFit: next,
                    },
                    'itemStyle',
                  )
                }
              />
            </SettingsRow>

            {(itemStyle?.displayCase || 'case pleine largeur') === 'masque' && (
              <>
                <PicSelectionOptions
                  title='Image de masque'
                  options={maskOptions}
                  value={
                    Number.isFinite(itemStyle?.bgMaskPicIndex)
                      ? itemStyle.bgMaskPicIndex
                      : 'none'
                  }
                  size={28}
                  onChange={value =>
                    updateGridField(
                      {
                        ...itemStyle,
                        bgMaskPicIndex: value === 'none' ? null : Number(value),
                      },
                      'itemStyle',
                    )
                  }
                />
                <MaskControls
                  fit={itemStyle?.bgMaskFit || 'contain'}
                  onFitChange={next =>
                    updateGridField(
                      {
                        ...itemStyle,
                        bgMaskFit: next,
                      },
                      'itemStyle',
                    )
                  }
                  invert={itemStyle?.bgMaskInvert === true}
                  onInvertChange={value =>
                    updateGridField(
                      {
                        ...itemStyle,
                        bgMaskInvert: value,
                      },
                      'itemStyle',
                    )
                  }
                  rotate={itemStyle?.bgMaskRotate ?? 0}
                  onRotateChange={next =>
                    updateGridField(
                      {
                        ...itemStyle,
                        bgMaskRotate: next,
                      },
                      'itemStyle',
                    )
                  }
                  includeFixed={true}
                />
              </>
            )}
          </WidgetSettingsCollapsibleSection>
        </WidgetSettingsCollapsibleSection>

        {displayTypeLink === 'complet' && (
          <WidgetSettingsCollapsibleSection
            title='Affichage (mode complet)'
            defaultOpen={false}
          >
            <SettingsRow label='Onglet affiché' dense>
              <SettingsSelect
                value={fullTabId}
                onChange={event => {
                  const nextValue = event.target.value;
                  setFullTabId(nextValue);
                  updateGridField(nextValue, 'fullTabId');
                }}
              >
                <option value='sidebar'>Sidebar (par défaut)</option>
                {fullTabOptions.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </SettingsSelect>
            </SettingsRow>
          </WidgetSettingsCollapsibleSection>
        )}
        {/* <InputSimple
          placeholder={'url'}
          initValue={filter?.url}
          onBlur={value => updateField(value, 'url')}
        /> */}
        <WidgetSettingsCollapsibleSection
          title='Liens requis'
          defaultOpen={false}
        >
          <div className='flex flex-wrap gap-05 text-sm text-contrast text-opacity-70'>
            Afficher tous les items ayant pour liens :
            {filterItems?.map((filterItem, idx) => (
              <div
                key={`required-${filterItem?._id || idx}`}
                className='flex items-center gap-05 bg-main bg-opacity-40 text-sm py-02 pl-1 pr-05 rounded-full'
              >
                {filterItem?.label}
                <div className='text-secondary transition hover:opacity-50 cursor-pointer'>
                  <FaTimes
                    onDoubleClick={() =>
                      updateField(
                        filter.items.filter(
                          itemIdx => itemIdx !== filterItem?._id,
                        ),
                        'items',
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <SearchInput
            itemId={itemId}
            searchIn='items'
            callback={addItem}
          />
          <div className='mt-05 flex flex-wrap gap-05'>
            <BtnSimple onClick={addCurrentItemToList}>
              Ajouter l'item courant
            </BtnSimple>
            <BtnSimple onClick={addCurrentItemToExclude}>
              Exclure l'item courant
            </BtnSimple>
            <BtnSimple onClick={removeCurrentItem}>
              Ne pas utiliser l'item courant
            </BtnSimple>
          </div>
        </WidgetSettingsCollapsibleSection>

        <WidgetSettingsCollapsibleSection title='Exclusions' defaultOpen={false}>
          <div className='flex flex-wrap gap-05 text-sm text-contrast text-opacity-70'>
            Exclure tous les items ayant pour liens :
            {excludeFilterItems?.map((excludFilterItem, idx) => (
              <div
                key={`exclude-${excludFilterItem?._id || idx}`}
                className='flex items-center gap-05 bg-main bg-opacity-40 text-sm py-02 pl-1 pr-05 rounded-full'
              >
                {excludFilterItem?.label}
                <div className='text-secondary transition hover:opacity-50 cursor-pointer'>
                  <FaTimes
                    onDoubleClick={() =>
                      updateField(
                        (Array.isArray(filter?.exclude) ? filter.exclude : []).filter(
                          itemIdx => itemIdx !== excludFilterItem?._id,
                        ),
                        'exclude',
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <SearchInput
            itemId={itemId}
            searchIn='items'
            callback={addExcludeItem}
          />
        </WidgetSettingsCollapsibleSection>
      </WidgetSettingsRoot>

      <DangerZone
        confirmId={'delete' + filterId + itemId}
        closeModalId={filterId}
        message='Voulez-vous vraiment supprimer ce filtre ?'
        onConfirm={deleteFilter}
      />
    </ModalGlobal>
  );
};
  const serialize = value => {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  };
